import { Input } from "antd";
import React, { useState, useRef, useEffect, useCallback } from "react";

export interface NumberInputProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
  max?: number;
  precision?: number;
  overflowBehavior?: "null" | "truncate";
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  suffix?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
}

function sanitizeNumericInput(rawValue: string, precision: number): string {
  const filteredValue = rawValue.replace(/[^\d.]/g, "");

  if (precision <= 0) {
    return filteredValue.replace(/\./g, "");
  }

  const firstDotIndex = filteredValue.indexOf(".");
  if (firstDotIndex === -1) {
    return filteredValue;
  }

  const integerPart = filteredValue.slice(0, firstDotIndex);
  const decimalPart = filteredValue
    .slice(firstDotIndex + 1)
    .replace(/\./g, "")
    .slice(0, precision);

  return `${integerPart}.${decimalPart}`;
}

function parseNumericValue(
  rawValue: string,
  precision: number,
  max?: number,
  overflowBehavior?: "null" | "truncate",
): number | null {
  const strValue = sanitizeNumericInput(rawValue, precision);

  if (strValue === "" || strValue === ".") {
    return null;
  }

  const numValue = Number(strValue);

  if (isNaN(numValue)) {
    return null;
  }

  if (max !== undefined && numValue > max) {
    if (overflowBehavior === "truncate") {
      return null;
    }
    return null;
  }

  if (precision > 0) {
    const parts = strValue.split(".");
    if (parts[1] && parts[1].length > precision) {
      const intPart = parts[0] || "0";
      return Number(intPart + "." + parts[1].slice(0, precision));
    }
    return numValue;
  }

  return parseInt(strValue, 10);
}

function formatNumericDisplayValue(
  rawValue: string,
  precision: number,
  max?: number,
  overflowBehavior?: "null" | "truncate",
): string {
  const parsedValue = parseNumericValue(
    rawValue,
    precision,
    max,
    overflowBehavior,
  );

  return parsedValue === null ? "" : String(parsedValue);
}

const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  disabled,
  max,
  precision = 0,
  overflowBehavior = "null",
  addonBefore,
  addonAfter,
  suffix,
  style,
  className,
  placeholder,
}) => {
  const [localValue, setLocalValue] = useState<string>(
    value != null ? String(value) : "",
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedRef = useRef<number | null>(value ?? null);

  // Sync external value changes back to local (e.g. on form reset)
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      setLocalValue(value != null ? String(value) : "");
      lastEmittedRef.current = value ?? null;
    }
  }, [value]);

  const emitChange = useCallback(
    (numValue: number | null) => {
      if (numValue === lastEmittedRef.current) {
        return;
      }
      lastEmittedRef.current = numValue;
      onChange?.(numValue);
    },
    [onChange],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = sanitizeNumericInput(e.target.value, precision);
    setLocalValue(rawValue);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const parsed = parseNumericValue(
      rawValue,
      precision,
      max,
      overflowBehavior,
    );

    timerRef.current = setTimeout(() => {
      emitChange(parsed);
    }, 150);
  };

  const handleBlur = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    const parsed = parseNumericValue(
      localValue,
      precision,
      max,
      overflowBehavior,
    );
    setLocalValue(
      formatNumericDisplayValue(localValue, precision, max, overflowBehavior),
    );
    emitChange(parsed);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <Input
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      addonBefore={addonBefore}
      addonAfter={addonAfter}
      suffix={suffix}
      style={style}
      className={className}
      placeholder={placeholder}
    />
  );
};

export default NumberInput;
